import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import classNames from "classnames";

import TextInput from "../../common/hookForm/TextInput";
import Dropdown from "../../common/hookForm/Dropdown";
import PrimaryButton from "../../common/PrimaryButton";
import Spinner from "../../common/Spinner";
import {
  getCitiesByProvince,
  getDistrictsByCity,
  getProvinces,
  lookupNationalAddress,
} from "../../../setup/api/services/address";
import { ICity } from "../../../setup/api/services/address/models";
import styles from "../styles.module.scss";
import { ClearIcon } from "../gateIcons";
import {
  NATIONAL_ADDRESS_PATTERN,
  normalizeNationalAddress,
} from "../organizationRegistration.constants";
import ActivationTaskModal from "./ActivationTaskModal";
import { IAddressDraft, IOption } from "./types";
import { isSavedAddressDraft } from "./drafts";

// TextInput requires onBlur; these fields have no blur behaviour.
const noop = () => undefined;

const toCityOption = (city: ICity): IOption => ({
  id: city.id,
  name: city.cityNameAr || city.cityNameEn,
});

/**
 * Keeps the selected option but takes its label from the loaded list, so a value pre-filled
 * from the lookup (which can come back in English) shows the same Arabic name as the list.
 */
const relabel = (selected: IOption | null, list: IOption[]) =>
  selected
    ? list.find((option) => option.id === selected.id) ?? selected
    : selected;

interface Props {
  visible: boolean;
  /** Previously saved address, when the user reopens the task to edit it. */
  initialValue: IAddressDraft | null;
  onSave: (value: IAddressDraft) => void;
  onClose: () => void;
}

/**
 * Activation task: the organization's national address. The user enters the short address
 * code, which is looked up to pre-fill region, city, district, street and postal code; they
 * review those and add it.
 */
const NationalAddressModal: React.FC<Props> = ({
  visible,
  initialValue,
  onSave,
  onClose,
}) => {
  const { t } = useTranslation();
  const [step, setStep] = useState<"code" | "details">("code");
  const [code, setCode] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [lookupFailed, setLookupFailed] = useState(false);

  const [province, setProvince] = useState<IOption | null>(null);
  const [city, setCity] = useState<IOption | null>(null);
  const [district, setDistrict] = useState<IOption | null>(null);
  const [street, setStreet] = useState("");
  const [postalCode, setPostalCode] = useState("");
  // Not in the design: carried through from the lookup, never asked for.
  const [buildingNo, setBuildingNo] = useState("");

  const [provinces, setProvinces] = useState<IOption[]>([]);
  const [cities, setCities] = useState<IOption[]>([]);
  const [districts, setDistricts] = useState<IOption[]>([]);
  const [loadingCities, setLoadingCities] = useState(false);
  const [loadingDistricts, setLoadingDistricts] = useState(false);

  // Every opening starts from what was last saved, so cancelling discards edits.
  useEffect(() => {
    if (!visible) return;
    setCode(initialValue?.nationalAddress ?? "");
    setProvince(initialValue?.province ?? null);
    setCity(initialValue?.city ?? null);
    setDistrict(initialValue?.district ?? null);
    setStreet(initialValue?.street ?? "");
    setPostalCode(initialValue?.postalCode ?? "");
    setBuildingNo(initialValue?.buildingNo ?? "");
    setLookupFailed(false);
    // A saved address from the server has no list ids, so it's re-verified from its code.
    setStep(
      initialValue && !isSavedAddressDraft(initialValue) ? "details" : "code"
    );
  }, [visible, initialValue]);

  const inDetails = visible && step === "details";

  useEffect(() => {
    if (!inDetails || provinces.length) return;
    getProvinces()
      .then((response) => {
        const list = (response?.data ?? []).map((p) => ({
          id: p.id,
          name: p.provinceNameAr || p.provinceNameEn,
        }));
        setProvinces(list);
        setProvince((prev) => relabel(prev, list));
      })
      .catch(() => setProvinces([]));
  }, [inDetails, provinces.length]);

  const provinceId = province?.id;
  useEffect(() => {
    if (!inDetails || !provinceId) {
      setCities([]);
      return;
    }
    let cancelled = false;
    setLoadingCities(true);
    getCitiesByProvince(provinceId)
      .then((response) => {
        if (cancelled) return;
        const list = (response?.data ?? []).map(toCityOption);
        setCities(list);
        setCity((prev) => relabel(prev, list));
      })
      .catch(() => !cancelled && setCities([]))
      .finally(() => !cancelled && setLoadingCities(false));
    return () => {
      cancelled = true;
    };
  }, [inDetails, provinceId]);

  const cityId = city?.id;
  useEffect(() => {
    if (!inDetails || !cityId) {
      setDistricts([]);
      return;
    }
    let cancelled = false;
    setLoadingDistricts(true);
    getDistrictsByCity(cityId)
      .then((response) => {
        if (cancelled) return;
        const list = (response?.data ?? []).map(toCityOption);
        setDistricts(list);
        setDistrict((prev) => relabel(prev, list));
      })
      .catch(() => !cancelled && setDistricts([]))
      .finally(() => !cancelled && setLoadingDistricts(false));
    return () => {
      cancelled = true;
    };
  }, [inDetails, cityId]);

  const isCodeValid = NATIONAL_ADDRESS_PATTERN.test(code);

  const onVerify = async () => {
    if (!isCodeValid || verifying) return;
    setVerifying(true);
    setLookupFailed(false);
    try {
      const { data } = await lookupNationalAddress(code);
      // The lookup's codes are the same ids the province/city/district lists use.
      setProvince(
        data.provinceCode
          ? { id: data.provinceCode, name: data.province }
          : null
      );
      setCity(data.cityCode ? { id: data.cityCode, name: data.city } : null);
      setDistrict(
        data.districtCode
          ? { id: data.districtCode, name: data.district }
          : null
      );
      setStreet(data.street ?? "");
      setPostalCode(data.postalCode ?? "");
      setBuildingNo(data.buildingNo ?? "");
      setStep("details");
    } catch {
      setLookupFailed(true);
    } finally {
      setVerifying(false);
    }
  };

  // Changing a parent invalidates its children.
  const onPickProvince = (option: IOption) => {
    if (option.id === province?.id) return;
    setProvince(option);
    setCity(null);
    setDistrict(null);
  };
  const onPickCity = (option: IOption) => {
    if (option.id === city?.id) return;
    setCity(option);
    setDistrict(null);
  };
  const onClearProvince = () => {
    setProvince(null);
    setCity(null);
    setDistrict(null);
  };
  const onClearCity = () => {
    setCity(null);
    setDistrict(null);
  };

  /** The design's chevron | clear (x) group on each address dropdown. */
  const withClear = (
    field: React.ReactNode,
    onClear: () => void,
    hasValue: boolean
  ) => (
    <div
      className={classNames(styles.addrSelect, {
        [styles.addrSelectFilled]: hasValue,
      })}>
      {field}
      {hasValue && (
        <>
          <span className={styles.addrSelectSep} />
          <button
            type="button"
            className={styles.addrSelectClear}
            aria-label={t("lbl_org_act_clear") as string}
            onClick={onClear}>
            <ClearIcon />
          </button>
        </>
      )}
    </div>
  );

  const canAdd =
    !!province &&
    !!city &&
    !!district &&
    street.trim() !== "" &&
    postalCode.trim() !== "";

  const onAdd = () => {
    if (!canAdd || !province || !city || !district) return;
    onSave({
      nationalAddress: code,
      province,
      city,
      district,
      street: street.trim(),
      postalCode: postalCode.trim(),
      buildingNo: buildingNo.trim(),
    });
  };

  return (
    <ActivationTaskModal
      visible={visible}
      onClose={onClose}
      title={t("lbl_org_act_addr_title")}>
      {step === "code" ? (
        <>
          <div className={styles.tmField}>
            <TextInput
              name="nationalAddress"
              dir="ltr"
              value={code}
              onChange={(e) => {
                setCode(normalizeNationalAddress(e.target.value));
                setLookupFailed(false);
              }}
              onBlur={noop}
              placeholder={t("lbl_org_act_addr_code") as string}
              error={
                lookupFailed
                  ? (t("lbl_org_act_addr_lookup_failed") as string)
                  : undefined
              }
              hint={t("lbl_org_act_addr_code_hint") as string}
            />
          </div>
          <div className={styles.tmStepAction}>
            <PrimaryButton
              type="button"
              disabled={!isCodeValid || verifying}
              onClick={onVerify}>
              {verifying ? <Spinner /> : t("lbl_org_act_addr_verify")}
            </PrimaryButton>
          </div>
        </>
      ) : (
        <>
          <div className={styles.addrStrip}>
            <span>
              <b>{t("lbl_org_act_addr_code")}</b>
              <span dir="ltr">{code || "—"}</span>
            </span>
            <button
              type="button"
              className={styles.textLink}
              onClick={() => setStep("code")}>
              {t("lbl_org_act_edit")}
            </button>
          </div>
          <div className={styles.addrGrid}>
            {withClear(
              <Dropdown
                name="province"
                data={provinces}
                value={province}
                onChange={onPickProvince}
                loading={!provinces.length}
                placeholder={t("lbl_org_act_addr_region") as string}
              />,
              onClearProvince,
              !!province
            )}
            {withClear(
              <Dropdown
                name="city"
                data={cities}
                value={city}
                onChange={onPickCity}
                loading={loadingCities}
                disabled={!province}
                placeholder={t("lbl_org_field_city") as string}
              />,
              onClearCity,
              !!city
            )}
          </div>
          <div className={styles.tmField}>
            {withClear(
              <Dropdown
                name="district"
                data={districts}
                value={district}
                onChange={setDistrict}
                loading={loadingDistricts}
                disabled={!city}
                placeholder={t("lbl_org_field_district") as string}
              />,
              () => setDistrict(null),
              !!district
            )}
          </div>
          <div className={styles.tmField}>
            <TextInput
              name="street"
              value={street}
              onChange={(e) => setStreet(e.target.value)}
              onBlur={noop}
              placeholder={t("lbl_org_field_street") as string}
            />
          </div>
          <div className={styles.tmField}>
            <TextInput
              name="postalCode"
              dir="ltr"
              inputType="number"
              disableDecimal
              value={postalCode}
              onChange={(e) => setPostalCode(e.target.value.slice(0, 10))}
              onBlur={noop}
              placeholder={t("lbl_org_field_postal_code") as string}
            />
          </div>
          <div className={styles.tmStepAction}>
            <PrimaryButton type="button" disabled={!canAdd} onClick={onAdd}>
              {t("lbl_org_act_addr_add")}
            </PrimaryButton>
          </div>
        </>
      )}
    </ActivationTaskModal>
  );
};

export default NationalAddressModal;
